import React, { useEffect, useState, useMemo } from "react";
import jsPDF from "jspdf";
import axiosInstance from "../../services/axiosInstance";

export default function DownloadNurseCertificate({
  filename="ADHD_CERTIFICATE",
}) {
  const [certificateInfo, setCertificateInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Map API payload -> certificate data for PDF
  const buildCertificate = (item) => {
    if (!item) return null;

    const user = item.userId || {};
    const modules = Array.isArray(item.modules) ? item.modules : [];
    const course = item.courseId || {};
    const createdAt = item.createdAt ? new Date(item.createdAt) : new Date();

    // readable date (YYYY-MM-DD)
    const completionDate = [
      createdAt.getFullYear(),
      String(createdAt.getMonth() + 1).padStart(2, "0"),
      String(createdAt.getDate()).padStart(2, "0"),
    ].join("-");

    // short code from _id
    const shortCode =
      item._id ? `CERT-${String(item._id).slice(-6).toUpperCase()}` : "CERT-XXXXXX";

    // Normalize module titles
    const moduleTitles = modules.map((m) =>
      typeof m === "string" ? m : (m?.title ?? "-")
    );

    return {
      org: "ADHD Rapid Care",
      name: user.name || "—",
      courseTitle: course.title || "ADHD Nursing Proficiency Course",
      duration: "4 Hours Self-Paced Learning",
      completionDate,
      certificateId: shortCode,
      modules: moduleTitles,
      clinicalDirector: "Dr. [Name], Clinical Director",
      trainer: "[Name], Training Coordinator",
      website: "www.adhdrapidcare.com",
      supportEmail: "admin@adhdrapidcare.com",
      yearLabel: `Certified ADHD Nurse Training ${createdAt.getFullYear()}`,
    };
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setIsLoading(true);
        // use latest=1 to get one document
        const res = await axiosInstance.get("/course/cretificate");
        const item = Array.isArray(res.data) ? res.data[0] : res.data;
        if (mounted) setCertificateInfo(buildCertificate(item));
      } catch (err) {
        console.error("Error fetching certificate:", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const data = useMemo(() => certificateInfo, [certificateInfo]);

  const dl = () => {
    if (!data) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    const brand = [25, 84, 130];
    const brandLt = [209, 229, 240];
    const text = [33, 37, 41];
    const mute = [95, 108, 123];
    const line = [224, 229, 235];
    const panel = [248, 250, 252];
    const M = 44;

    // Panel
    doc.setDrawColor(...line);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(M, M, W - 2 * M, H - 2 * M, 14, 14, "FD");

    // Thin top divider
    doc.setDrawColor(...brandLt);
    doc.setLineWidth(1);
    doc.line(M + 8, M + 8, W - M - 8, M + 8);

    // Header
    const headerY = M + 36;
    doc.setFillColor(...brand);
    doc.circle(W / 2 - 80, headerY - 6, 4, "F");
    doc.setTextColor(...brand);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(data.org || "-", W / 2 - 68, headerY);

    doc.setTextColor(...text);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(26);
    doc.text("Certificate of Completion", W / 2, headerY + 36, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...mute);
    doc.setFontSize(12);
    doc.text("This certificate is proudly awarded to", W / 2, headerY + 60, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...brand);
    doc.setFontSize(30);
    doc.text(data.name || "-", W / 2, headerY + 96, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...text);
    doc.setFontSize(12);
    doc.text(
      "For successfully completing the ADHD Nursing Proficiency Course",
      W / 2,
      headerY + 120,
      { align: "center" }
    );

    // === Single info box (Course Title on top; below two cols: Completion Date & Certificate ID) ===
    const pillY = headerY + 152;
    const pillW = (W - 2 * M - 32);   // keep the half-width look on the left
    const leftX = M + 16;

    const drawInfoBox = (x, y, { titleLabel, titleValue, leftLabel, leftValue, rightLabel, rightValue }) => {
      const titleLines = doc.splitTextToSize(String(titleValue || "-"), pillW - 36);
      const localPillH = Math.max(110, 70 + (titleLines.length - 1) * 14);

      doc.setFillColor(...panel);
      doc.setDrawColor(...brandLt);
      doc.roundedRect(x, y, pillW, localPillH, 16, 16, "FD");

      let ty = y + 24;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...mute);
      doc.setFontSize(11);
      doc.text(titleLabel, x + 18, ty);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...text);
      doc.setFontSize(12);
      ty += 18;
      titleLines.forEach((ln) => {
        doc.text(ln, x + 18, ty, { maxWidth: pillW - 36 });
        ty += 14;
      });

      // Divider
      const dividerY = ty + 8;
      doc.setDrawColor(...brandLt);
      doc.setLineWidth(0.8);
      doc.line(x + 14, dividerY, x + pillW - 14, dividerY);

      // Two columns
      const colGap = 24;
      const colW = (pillW - 36 - colGap) / 2;
      const leftColX = x + 18;
      const rightColX = leftColX + colW + colGap;
      const colY = dividerY + 20;

      // Left
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...mute);
      doc.setFontSize(11);
      doc.text(leftLabel, leftColX, colY);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...text);
      doc.setFontSize(12);
      doc.text(String(leftValue || "-"), leftColX, colY + 18, { maxWidth: colW });

      // Right
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...mute);
      doc.setFontSize(11);
      doc.text(rightLabel, rightColX, colY);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...text);
      doc.setFontSize(12);
      doc.text(String(rightValue || "-"), rightColX, colY + 18, { maxWidth: colW });

      // Optional vertical separator
      doc.setDrawColor(...brandLt);
      doc.setLineWidth(0.6);
      doc.line(leftColX + colW + colGap / 2, dividerY , leftColX + colW + colGap / 2, dividerY + 45);



      return localPillH;
    };

    const pillH = drawInfoBox(leftX, pillY, {
      titleLabel: "Course Title:",
      titleValue: data.courseTitle,
      leftLabel: "Completion Date:",
      leftValue: data.completionDate,
      rightLabel: "Certificate ID:",
      rightValue: data.certificateId,
    });

    // === Modules ===
    const modulesTitleY = pillY + pillH + 40;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...text);
    doc.setFontSize(14);
    doc.text("Completed Modules:", M + 22, modulesTitleY);
 
    const modGapX = 20;
    const modColW = (W - 3 * M - modGapX) / 2;
    const modStartY = modulesTitleY + 18;

    const drawModule = (x, y, textStr) => {
      const h = 40;
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...brandLt);
      doc.roundedRect(x, y, modColW, h, 12, 12, "FD");
      doc.setFillColor(...brand);
      doc.circle(x + 16, y + h / 2, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("✓", x + 12.5, y + h / 2 + 3.5);
      doc.setTextColor(...text);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(String(textStr || "-"), x + 32, y + h / 2.5, { maxWidth: modColW - 44 });
    };

    // Render modules, cap to fit the page; add a "+N more" bubble if needed
    const maxRows = 5; // ~2 columns * 5 rows = ~10 bubbles safely on one page
    const maxVisible = maxRows * 2;
    const allMods = Array.isArray(data.modules) ? data.modules : [];
    const visible = allMods.slice(0, maxVisible);
    const remaining = Math.max(0, allMods.length - visible.length);

    const rowH = 52;
    for (let i = 0; i < visible.length; i++) {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = M + 22 + col * (modColW + modGapX);
      const y = modStartY + row * rowH;
      drawModule(x, y, visible[i]);
    }

    if (remaining > 0) {
      const i = visible.length;
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = M + 22 + col * (modColW + modGapX);
      const y = modStartY + row * rowH;
      drawModule(x, y, `+${remaining} more`);
    }

    const modsRowsUsed = Math.ceil(Math.min(visible.length + (remaining > 0 ? 1 : 0), maxVisible + 1) / 2);
    const sigBaseY = modStartY + modsRowsUsed * rowH + 48;


    // Footer
    const footY = H - M + 10;
    doc.setTextColor(...mute);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(data.website || "", W / 2, footY, { align: "center" });

    const disclaim =
      "This certificate is a digital proof of course completion and does not replace formal accreditation/licensure requirements.";
    doc.setFontSize(8.5);
    doc.setTextColor(120, 126, 134);
    doc.text(disclaim, W / 2, footY + 16, { align: "center", maxWidth: W - 2 * M });

    if (data.supportEmail) {
      doc.setFontSize(9);
      doc.setTextColor(...mute);
      doc.text(data.supportEmail, W / 2, footY - 14, { align: "center" });
    }

    const safeName = (data.name || "nurse_certificate").replace(/\s+/g, "_");
    doc.save((filename || `${safeName}_certificate.pdf`).replace(/\s+/g, "_"));
  };

  return (
    <button
      onClick={dl}
      disabled={isLoading || !data}
      className="bg-blue-600 text-white px-6 py-3 mt-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
      type="button"
    >
      {isLoading ? "Loading…" : !data ? "Preparing…" : "Download PDF"}
    </button>
  );
}
